import React, { useState, useMemo, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronDown, ChevronUp, Award, Layers, Target, BarChart3, GitBranch, GraduationCap, Settings } from 'lucide-react';
import GraphSidebar, { COLOR_SCHEMES } from '@/components/GraphSidebar';

const CC = ['#2ECC71','#E74C3C','#3498DB','#F39C12','#9B59B6','#1ABC9C','#E67E22','#C0392B','#2980B9','#8E44AD'];

const THEMES = {
  nature: { bg: '#F9F8F6', card: '#FFFFFF', accent: '#6B8E7B', accent2: '#2ECC71', text: '#2C2A28', muted: '#8A8580', header: '#EAF0EC' },
  academic: { bg: '#F0F4F8', card: '#FFFFFF', accent: '#2980B9', accent2: '#3498DB', text: '#1A202C', muted: '#718096', header: '#EBF8FF' },
  saffron: { bg: '#FFFAF0', card: '#FFFFFF', accent: '#C87A5E', accent2: '#F39C12', text: '#2D1B0E', muted: '#A07850', header: '#FEF5E7' },
};

const SYMBOLS = ['circle', 'diamond', 'square', 'triangle-up', 'star', 'cross', 'pentagon', 'hexagon'];

export default function ClusterAnalysisView({ data }) {
  const [method, setMethod] = useState('tocher');
  const [metric, setMetric] = useState('mahalanobis');
  const [kValue, setKValue] = useState(data?.optimal_k?.suggested_k || 3);
  const [dendrogramCutoff, setDendrogramCutoff] = useState(null);
  const [themeName, setThemeName] = useState('nature');
  const [symbolMode, setSymbolMode] = useState('circle');
  const [showClassroom, setShowClassroom] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ anova: true, clusters: true, distances: true, traits: true, means: true, recs: true, dendrogram: true, scatter: true, optimal: true });
  const [colorScheme, setColorScheme] = useState('default');
  const [labelFontSize, setLabelFontSize] = useState(11);

  const theme = THEMES[themeName];
  const COLORS = COLOR_SCHEMES[colorScheme]?.colors || CC;
  const toggle = useCallback((k) => setExpandedSections(p => ({ ...p, [k]: !p[k] })), []);

  // Get current clustering result based on selected method
  const currentClusters = useMemo(() => {
    if (method === 'tocher') return data.tocher;
    if (method === 'ward') return data.ward;
    if (method === 'upgma') return data.upgma;
    if (method === 'kmeans') return data.kmeans?.[String(kValue)] || {};
    return data.tocher;
  }, [method, kValue, data]);

  const currentGenoMap = useMemo(() => {
    if (method === 'kmeans') return currentClusters?.geno_map || {};
    return currentClusters?.geno_map || {};
  }, [method, currentClusters]);

  const currentClusterList = useMemo(() => {
    return currentClusters?.clusters || {};
  }, [currentClusters]);

  const nClusters = Object.keys(currentClusterList).length;

  const Section = ({ id, icon: Icon, title, badge, children }) => (
    <Card className="border shadow-sm overflow-hidden" style={{ borderColor: `${theme.accent}30`, backgroundColor: theme.card }}>
      <CardHeader className="cursor-pointer hover:opacity-80 transition-opacity" onClick={() => toggle(id)}>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-base" style={{ color: theme.text }}>
            <Icon size={18} style={{ color: theme.accent }} />
            {title}
            {badge && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: theme.header, color: theme.text }}>{badge}</span>}
          </CardTitle>
          {expandedSections[id] ? <ChevronUp size={16} style={{ color: theme.muted }} /> : <ChevronDown size={16} style={{ color: theme.muted }} />}
        </div>
      </CardHeader>
      {expandedSections[id] && <CardContent>{children}</CardContent>}
    </Card>
  );

  // ---- ANOVA ----
  const renderANOVA = () => {
    const anova = data.pooled_anova;
    if (!anova) return null;
    return (
      <Section id="anova" icon={BarChart3} title="Pooled ANOVA" badge={`${Object.keys(anova).length} traits`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="cluster-anova-table">
            <thead><tr style={{ backgroundColor: theme.header }}>
              {['Trait','DF(T)','DF(E)','SS(T)','MS(T)','MS(E)','F','P','Sig'].map(h => (
                <th key={h} className="border px-3 py-2 text-left font-semibold text-xs uppercase" style={{ borderColor: `${theme.accent}30` }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {Object.entries(anova).map(([trait, r]) => (
                <tr key={trait} className="hover:opacity-80">
                  <td className="border px-3 py-2 font-medium text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.text }}>{trait}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.DF_Treatment}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.DF_Error}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.SS_Treatment?.toFixed(2)}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.MS_Treatment?.toFixed(4)}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.MS_Error?.toFixed(4)}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.F?.toFixed(4)}</td>
                  <td className="border px-3 py-2 text-right font-mono text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.muted }}>{r.P?.toFixed(6)}</td>
                  <td className="border px-3 py-2 text-center" style={{ borderColor: `${theme.accent}20` }}>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.Sig === '**' ? 'bg-emerald-500 text-white' : r.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{r.Sig}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    );
  };

  // ---- Cluster Members ----
  const renderClusterMembers = () => (
    <Section id="clusters" icon={Layers} title={`${method.charAt(0).toUpperCase() + method.slice(1)} Clusters`} badge={`${nClusters} clusters`}>
      <div className="space-y-3" data-testid="cluster-members">
        {Object.entries(currentClusterList).map(([name, info], idx) => (
          <div key={name} className="rounded-2xl p-4 border" style={{ backgroundColor: `${theme.header}80`, borderColor: `${theme.accent}30` }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
              <span className="font-semibold text-sm" style={{ color: theme.text }}>{name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: theme.header, color: theme.muted }}>{info.count} genotype{info.count > 1 ? 's' : ''}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 ml-7">
              {info.members.map(m => (
                <span key={m} className="inline-block bg-white border px-2.5 py-1 rounded-lg text-xs font-mono" style={{ borderColor: `${theme.accent}40`, color: theme.text }}>{m}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );

  // ---- Trait Contribution ----
  const renderTraitContribution = () => {
    const tc = data.trait_contributions;
    if (!tc) return null;
    const sorted = Object.entries(tc).sort((a, b) => a[1].rank - b[1].rank);
    return (
      <Section id="traits" icon={BarChart3} title="Trait Contribution to D²" badge="Times ranked 1st">
        <div className="space-y-2" data-testid="cluster-trait-contribution">
          {sorted.map(([trait, info]) => (
            <div key={trait} className="flex items-center gap-3">
              <span className="w-6 text-center text-xs font-bold" style={{ color: theme.muted }}>#{info.rank}</span>
              <span className="w-40 text-sm font-medium truncate" style={{ color: theme.text }}>{trait}</span>
              <div className="flex-1 rounded-full h-6 relative overflow-hidden" style={{ backgroundColor: theme.header }}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(info.percentage, 2)}%`, backgroundColor: info.percentage > 20 ? theme.accent2 : theme.accent }} />
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ color: theme.text }}>{info.percentage.toFixed(2)}%</span>
              </div>
              {info.times_ranked_first != null && (
                <span className="w-20 text-right text-xs" style={{ color: theme.muted }}>{info.times_ranked_first}× 1st</span>
              )}
            </div>
          ))}
        </div>
      </Section>
    );
  };

  // ---- Cluster Means vs Population ----
  const renderClusterMeans = () => {
    const cm = currentClusters?.cluster_means || data.tocher?.cluster_means;
    const pm = data.population_means;
    if (!cm || !pm) return null;
    const clusters = Object.keys(cm);
    const traits = Object.keys(pm);
    return (
      <Section id="means" icon={BarChart3} title="Cluster Means vs Population" badge={`${traits.length} traits`}>
        <div className="overflow-x-auto" data-testid="cluster-means-table">
          <table className="w-full text-sm border-collapse">
            <thead><tr style={{ backgroundColor: theme.header }}>
              <th className="border px-3 py-2 text-left font-semibold text-xs" style={{ borderColor: `${theme.accent}30` }}>Cluster</th>
              {traits.map(t => (<th key={t} className="border px-2 py-2 text-center font-semibold text-xs" style={{ borderColor: `${theme.accent}30` }}>{t}</th>))}
            </tr></thead>
            <tbody>
              {clusters.map(c => (
                <tr key={c}>
                  <td className="border px-3 py-2 font-semibold text-xs" style={{ borderColor: `${theme.accent}20`, color: theme.text }}>{c}</td>
                  {traits.map(t => {
                    const val = cm[c]?.[t];
                    const popVal = pm[t];
                    const isAbove = val > popVal;
                    return (
                      <td key={t} className="border px-2 py-2 text-center font-mono text-xs" style={{
                        borderColor: `${theme.accent}20`,
                        backgroundColor: isAbove ? `${theme.accent2}15` : '',
                        color: isAbove ? theme.accent2 : theme.muted,
                        fontWeight: isAbove ? 'bold' : 'normal'
                      }}>{typeof val === 'number' ? val.toFixed(2) : '-'}</td>
                    );
                  })}
                </tr>
              ))}
              <tr style={{ backgroundColor: theme.header }}>
                <td className="border px-3 py-2 font-bold text-xs" style={{ borderColor: `${theme.accent}30`, color: theme.text }}>Population Mean</td>
                {traits.map(t => (
                  <td key={t} className="border px-2 py-2 text-center font-mono text-xs font-bold" style={{ borderColor: `${theme.accent}30`, color: theme.text }}>{pm[t]?.toFixed(2)}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="text-xs mt-2" style={{ color: theme.muted }}>Highlighted = above population mean</p>
        </div>
      </Section>
    );
  };

  // ---- Recommendations ----
  const renderRecommendations = () => {
    const recs = data.parental_recommendations;
    if (!recs?.length) return null;
    return (
      <Section id="recs" icon={Award} title="Parental Recommendations" badge="Auto-generated">
        <div className="space-y-3" data-testid="cluster-recommendations">
          {recs.map((rec, i) => (
            <div key={i} className="rounded-2xl p-5 border" style={{ backgroundColor: `${theme.header}80`, borderColor: `${theme.accent}30` }}>
              <p className="text-sm font-semibold mb-3" style={{ color: theme.text }}>For <span style={{ color: theme.accent }}>{rec.trait}</span>:</p>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="bg-white rounded-xl px-4 py-2.5 border text-center" style={{ borderColor: `${theme.accent}30` }}>
                  <p className="text-xs mb-1" style={{ color: theme.muted }}>{rec.parent_1_cluster}</p>
                  <p className="font-bold text-lg" style={{ color: theme.text }}>{rec.parent_1}</p>
                  <p className="text-xs font-mono" style={{ color: theme.accent }}>{rec.parent_1_value}</p>
                </div>
                <span className="text-2xl font-bold" style={{ color: theme.accent2 }}>&times;</span>
                <div className="bg-white rounded-xl px-4 py-2.5 border text-center" style={{ borderColor: `${theme.accent}30` }}>
                  <p className="text-xs mb-1" style={{ color: theme.muted }}>{rec.parent_2_cluster}</p>
                  <p className="font-bold text-lg" style={{ color: theme.text }}>{rec.parent_2}</p>
                  <p className="text-xs font-mono" style={{ color: theme.accent }}>{rec.parent_2_value}</p>
                </div>
                <span className="text-xs ml-2" style={{ color: theme.muted }}>D = <span className="font-mono font-bold" style={{ color: theme.accent2 }}>{rec.inter_cluster_distance}</span></span>
              </div>
            </div>
          ))}
        </div>
      </Section>
    );
  };

  // ---- Elbow + Silhouette ----
  const renderOptimalK = () => {
    const ok = data.optimal_k;
    if (!ok) return null;
    return (
      <Section id="optimal" icon={Target} title="Optimal Cluster Suggestion" badge={`k=${ok.suggested_k}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Plot
            data={[{ x: ok.elbow.map(d => d.k), y: ok.elbow.map(d => d.inertia), mode: 'lines+markers', marker: { color: theme.accent, size: 10 }, line: { color: theme.accent, width: 3 }, name: 'Inertia' }]}
            layout={{ title: { text: 'Elbow Plot', font: { size: 14, color: theme.text } }, xaxis: { title: 'k', dtick: 1 }, yaxis: { title: 'Inertia' }, paper_bgcolor: theme.bg, plot_bgcolor: theme.card, height: 300, margin: { t: 40, b: 50, l: 60, r: 20 } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
          <Plot
            data={[{ x: ok.silhouette.map(d => d.k), y: ok.silhouette.map(d => d.score), mode: 'lines+markers', marker: { color: theme.accent2, size: 10 }, line: { color: theme.accent2, width: 3 }, name: 'Silhouette' }]}
            layout={{ title: { text: 'Silhouette Score', font: { size: 14, color: theme.text } }, xaxis: { title: 'k', dtick: 1 }, yaxis: { title: 'Score' }, paper_bgcolor: theme.bg, plot_bgcolor: theme.card, height: 300, margin: { t: 40, b: 50, l: 60, r: 20 }, shapes: [{ type: 'line', x0: ok.suggested_k, x1: ok.suggested_k, y0: 0, y1: 1, line: { color: 'red', dash: 'dash', width: 2 } }] }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </div>
      </Section>
    );
  };

  // ---- Interactive Dendrogram ----
  const renderDendrogram = () => {
    const linkData = method === 'upgma' ? data.upgma?.linkage : data.ward?.linkage;
    const labels = data.genotype_labels;
    if (!linkData?.length || !labels?.length) return null;

    const n = labels.length;
    const maxH = Math.max(...linkData.map(l => l[2]));
    const cutoff = dendrogramCutoff ?? maxH * 0.5;

    const xCoords = {};
    const plotTraces = [];
    for (let i = 0; i < n; i++) xCoords[i] = i;

    for (let s = 0; s < linkData.length; s++) {
      const [c1, c2, dist] = linkData[s];
      const nc = n + s;
      const x1 = xCoords[c1] ?? 0;
      const x2 = xCoords[c2] ?? 0;
      xCoords[nc] = (x1 + x2) / 2;
      const y1 = c1 < n ? 0 : linkData[c1 - n]?.[2] || 0;
      const y2 = c2 < n ? 0 : linkData[c2 - n]?.[2] || 0;
      plotTraces.push({
        x: [x1, x1, x2, x2], y: [y1, dist, dist, y2],
        mode: 'lines', line: { color: dist > cutoff ? '#E74C3C' : theme.accent, width: 2 },
        hoverinfo: 'text', text: `D²=${dist.toFixed(2)}`, showlegend: false
      });
    }

    return (
      <Section id="dendrogram" icon={GitBranch} title={`Dendrogram (${method === 'upgma' ? 'UPGMA' : "Ward's"})`} badge="Interactive">
        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium" style={{ color: theme.muted }}>Cut-off:</label>
          <input type="range" min={0} max={maxH * 1.1} step={0.1} value={cutoff} onChange={e => setDendrogramCutoff(parseFloat(e.target.value))}
            className="w-48" style={{ accentColor: theme.accent }} data-testid="dendrogram-cutoff" />
          <span className="font-mono text-sm px-3 py-1 rounded-full" style={{ backgroundColor: theme.header, color: theme.text }}>{cutoff.toFixed(2)}</span>
        </div>
        <Plot
          data={[
            ...plotTraces,
            { x: Array.from({ length: n }, (_, i) => i), y: Array(n).fill(0), mode: 'markers+text', type: 'scatter',
              marker: { size: 10, color: theme.accent, symbol: symbolMode }, text: labels, textposition: 'bottom center',
              textfont: { size: 9, family: 'monospace' }, showlegend: false, hoverinfo: 'text' },
            { x: [-0.5, n - 0.5], y: [cutoff, cutoff], mode: 'lines', line: { color: '#E74C3C', width: 2, dash: 'dash' }, name: `Cutoff (${cutoff.toFixed(1)})` }
          ]}
          layout={{ xaxis: { showticklabels: false, zeroline: false }, yaxis: { title: 'D² Distance' }, paper_bgcolor: theme.bg, plot_bgcolor: theme.card, height: 420, margin: { t: 20, b: 50, l: 60, r: 20 }, showlegend: true, legend: { x: 0.75, y: 1 } }}
          config={{ responsive: true, displayModeBar: true, modeBarButtonsToRemove: ['lasso2d', 'select2d'] }}
          style={{ width: '100%' }}
        />
      </Section>
    );
  };

  // ---- 3D Scatter with Convex Hulls ----
  const render3DScatter = () => {
    const pts = data.pca_3d_data;
    const varExp = data.pca_variance_explained || [];
    const hulls = data.convex_hulls || {};
    if (!pts?.length) return null;

    // Re-map clusters to current method's geno_map
    const remapped = pts.map(p => ({ ...p, cluster: currentGenoMap[p.genotype] || p.cluster }));
    const clusters = [...new Set(remapped.map(p => p.cluster))].sort();

    const traces = clusters.map(cl => {
      const cpts = remapped.filter(p => p.cluster === cl);
      return {
        x: cpts.map(p => p.pc1), y: cpts.map(p => p.pc2), z: cpts.map(p => p.pc3),
        mode: 'markers+text', type: 'scatter3d', name: `Cluster ${cl}`,
        text: cpts.map(p => p.genotype), textposition: 'top center', textfont: { size: labelFontSize, family: 'monospace' },
        marker: { size: 7, color: COLORS[(cl - 1) % COLORS.length], opacity: 0.85, symbol: symbolMode, line: { color: '#2C2A28', width: 1 } },
        hovertemplate: '%{text}<br>PC1:%{x:.3f}<br>PC2:%{y:.3f}<br>PC3:%{z:.3f}<extra>Cluster ' + cl + '</extra>'
      };
    });

    // Add convex hulls as mesh3d if available
    clusters.forEach(cl => {
      const hullPts = hulls[String(cl)];
      if (hullPts?.length > 2) {
        traces.push({
          x: hullPts.map(p => p[0]), y: hullPts.map(p => p[1]), z: hullPts.map(() => 0),
          mode: 'lines', type: 'scatter3d', name: `Hull ${cl}`,
          line: { color: COLORS[(cl - 1) % COLORS.length], width: 3, dash: 'dot' }, showlegend: false
        });
      }
    });

    return (
      <Section id="scatter" icon={Target} title="3D PCA Scatter" badge="Zoom & Rotate">
        <div className="flex gap-4">
          <div className="flex-1">
            <Plot
              data={traces}
              layout={{
                scene: {
                  xaxis: { title: `PC1 (${varExp[0] || 0}%)`, backgroundcolor: theme.bg },
                  yaxis: { title: `PC2 (${varExp[1] || 0}%)`, backgroundcolor: theme.bg },
                  zaxis: { title: `PC3 (${varExp[2] || 0}%)`, backgroundcolor: theme.bg },
                },
                paper_bgcolor: theme.bg, margin: { t: 20, b: 20, l: 20, r: 20 }, height: 520, showlegend: true
              }}
              config={{ responsive: true, displayModeBar: true, scrollZoom: true, toImageButtonOptions: { format: 'png', width: 1800, height: 1200, scale: 2.5 } }}
              style={{ width: '100%' }}
            />
          </div>
          <div className="w-52 shrink-0">
            <GraphSidebar
              colorScheme={colorScheme}
              onColorSchemeChange={setColorScheme}
              fontSize={labelFontSize}
              onFontSizeChange={setLabelFontSize}
              onReset={() => { setColorScheme('default'); setLabelFontSize(11); }}
            />
          </div>
        </div>
      </Section>
    );
  };

  // ---- PhD Classroom ----
  const renderClassroom = () => {
    if (!data.classroom_derivation) return null;
    return (
      <div className="rounded-[20px] border-2 p-6 mb-4" style={{ borderColor: theme.accent, backgroundColor: `${theme.header}80` }} data-testid="classroom-panel">
        <div className="flex items-center gap-3 mb-4">
          <GraduationCap size={24} style={{ color: theme.accent }} />
          <h3 className="font-heading text-lg font-medium" style={{ color: theme.text }}>PhD Classroom: Step-by-Step D² Derivation</h3>
        </div>
        <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed" style={{ color: theme.text }}>{data.classroom_derivation}</pre>
      </div>
    );
  };

  // ---- Method Comparison (split-screen) ----
  const renderComparison = () => {
    const t = data.tocher?.clusters || {};
    const w = data.ward?.clusters || {};
    return (
      <Card className="border shadow-sm" style={{ borderColor: `${theme.accent}30` }}>
        <CardHeader><CardTitle className="text-base" style={{ color: theme.text }}>Tocher vs Ward: Method Comparison</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4" data-testid="method-comparison">
            <div className="rounded-xl border p-4" style={{ borderColor: `${theme.accent}40` }}>
              <h4 className="font-semibold text-sm mb-3" style={{ color: theme.accent }}>Tocher's Method ({Object.keys(t).length} clusters)</h4>
              {Object.entries(t).map(([name, info]) => (
                <div key={name} className="mb-2">
                  <span className="text-xs font-semibold" style={{ color: theme.text }}>{name}: </span>
                  <span className="text-xs font-mono" style={{ color: theme.muted }}>{info.members.join(', ')}</span>
                </div>
              ))}
            </div>
            <div className="rounded-xl border p-4" style={{ borderColor: `${theme.accent2}40` }}>
              <h4 className="font-semibold text-sm mb-3" style={{ color: theme.accent2 }}>Ward's Method ({Object.keys(w).length} clusters)</h4>
              {Object.entries(w).map(([name, info]) => (
                <div key={name} className="mb-2">
                  <span className="text-xs font-semibold" style={{ color: theme.text }}>{name}: </span>
                  <span className="text-xs font-mono" style={{ color: theme.muted }}>{info.members.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-5" style={{ color: theme.text }} data-testid="cluster-analysis-view">
      {/* Controls Bar */}
      <div className="rounded-2xl border p-5" style={{ backgroundColor: theme.card, borderColor: `${theme.accent}30` }}>
        <div className="flex flex-wrap items-center gap-4">
          {/* Method */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5" style={{ color: theme.muted }}>Method</label>
            <div className="flex gap-1">
              {[['tocher', "Tocher's"], ['ward', "Ward's"], ['upgma', 'UPGMA'], ['kmeans', 'K-Means']].map(([val, label]) => (
                <button key={val} onClick={() => setMethod(val)} data-testid={`method-${val}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${method === val ? 'text-white shadow-md' : 'hover:opacity-70'}`}
                  style={method === val ? { backgroundColor: theme.accent } : { backgroundColor: theme.header, color: theme.muted }}>{label}</button>
              ))}
            </div>
          </div>
          {/* K value (K-Means) */}
          {method === 'kmeans' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5" style={{ color: theme.muted }}>k</label>
              <select value={kValue} onChange={e => setKValue(parseInt(e.target.value))} className="border rounded-lg px-3 py-1.5 text-xs"
                style={{ borderColor: `${theme.accent}40` }} data-testid="k-value-select">
                {Object.keys(data.kmeans || {}).map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          )}
          {/* Distance Metric */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5" style={{ color: theme.muted }}>Distance</label>
            <div className="flex gap-1">
              {[['mahalanobis', 'D²'], ['euclidean', 'Euclidean'], ['manhattan', 'Manhattan']].map(([val, label]) => (
                <button key={val} onClick={() => setMetric(val)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${metric === val ? 'text-white' : 'hover:opacity-70'}`}
                  style={metric === val ? { backgroundColor: theme.accent2 } : { backgroundColor: theme.header, color: theme.muted }}>{label}</button>
              ))}
            </div>
          </div>
          {/* Theme */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5" style={{ color: theme.muted }}>Theme</label>
            <div className="flex gap-1">
              {[['nature', 'Nature'], ['academic', 'Academic'], ['saffron', 'Saffron']].map(([val, label]) => (
                <button key={val} onClick={() => setThemeName(val)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${themeName === val ? 'ring-2 ring-offset-1' : 'hover:opacity-70'}`}
                  style={{ backgroundColor: THEMES[val].header, color: THEMES[val].text, ringColor: THEMES[val].accent }}>{label}</button>
              ))}
            </div>
          </div>
          {/* Symbol */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider block mb-1.5" style={{ color: theme.muted }}>Symbol</label>
            <select value={symbolMode} onChange={e => setSymbolMode(e.target.value)} className="border rounded-lg px-3 py-1.5 text-xs" style={{ borderColor: `${theme.accent}40` }}>
              {SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* Toggles */}
          <div className="flex gap-2 ml-auto">
            <button onClick={() => setShowClassroom(!showClassroom)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${showClassroom ? 'text-white' : ''}`}
              style={showClassroom ? { backgroundColor: theme.accent } : { backgroundColor: theme.header, color: theme.muted }} data-testid="toggle-classroom">
              <GraduationCap size={14} /> PhD Mode
            </button>
            <button onClick={() => setCompareMode(!compareMode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${compareMode ? 'text-white' : ''}`}
              style={compareMode ? { backgroundColor: theme.accent2 } : { backgroundColor: theme.header, color: theme.muted }} data-testid="toggle-compare">
              <Settings size={14} /> Compare
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="rounded-[20px] p-6 border" style={{ backgroundColor: theme.header, borderColor: `${theme.accent}30` }} data-testid="cluster-summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Genotypes', value: data.n_genotypes, color: theme.text },
            { label: 'Traits', value: data.n_traits, color: theme.accent },
            { label: 'Clusters', value: nClusters, color: theme.accent2 },
            { label: 'Suggested k', value: data.optimal_k?.suggested_k, color: theme.text },
          ].map(s => (
            <div key={s.label} className="text-center">
              <p className="text-xs uppercase tracking-wider mb-1" style={{ color: theme.muted }}>{s.label}</p>
              <p className="text-3xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
        {data.interpretation && <p className="mt-4 text-sm leading-relaxed border-t pt-4" style={{ borderColor: `${theme.accent}30`, color: theme.muted }}>{data.interpretation}</p>}
      </div>

      {showClassroom && renderClassroom()}
      {compareMode && renderComparison()}
      {renderANOVA()}
      {renderClusterMembers()}
      {renderTraitContribution()}
      {renderClusterMeans()}
      {renderRecommendations()}
      {renderOptimalK()}
      {renderDendrogram()}
      {render3DScatter()}

      {data.chart && (
        <Card className="border shadow-sm" style={{ borderColor: `${theme.accent}30` }}>
          <CardHeader><CardTitle className="text-base">Static Charts</CardTitle></CardHeader>
          <CardContent><img src={`data:image/png;base64,${data.chart}`} alt="Charts" className="w-full rounded-xl" data-testid="cluster-static-chart" /></CardContent>
        </Card>
      )}
    </div>
  );
}
